import { useState, useEffect, useMemo, useRef } from 'react';
import { format, subMonths, addMonths, isSameDay, isWithinInterval, isAfter, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { calculateDateRange, formatDate } from '../../utils/dateRangeUtils';

/**
 * Selector de fechas mejorado (popup con calendario y períodos).
 * Usado en la barra de filtros de analytics y en Analytics20.
 */
export default function EnhancedDateRangeSelector({
  isOpen,
  onClose,
  selectedPeriod,
  onPeriodChange,
  customStartDate,
  customEndDate,
  onCustomDatesChange,
  isCompareEnabled = false,
  _onCompareToggle = () => {},
  compareType = 'previous',
  _onCompareTypeChange = () => {},
  customCompareStartDate = '',
  customCompareEndDate = '',
  onCustomCompareDatesChange = () => {},
  periods,
  _customComparePeriodLabel = '',
  onApplyFilters = () => {},
}) {
  const today = new Date();
  const [tempStartDate, setTempStartDate] = useState(customStartDate || '');
  const [tempEndDate, setTempEndDate] = useState(customEndDate || '');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const period = periods.find((p) => p.key === selectedPeriod);
    if (period && period.key !== 'CUSTOM') {
      const range = calculateDateRange(selectedPeriod, period.days, customStartDate, customEndDate);
      if (range && range.startDate) {
        return new Date(range.startDate.getFullYear(), range.startDate.getMonth(), 1);
      }
    }
    if (customStartDate) {
      const start = new Date(customStartDate);
      return new Date(start.getFullYear(), start.getMonth(), 1);
    }
    return new Date();
  });
  const [selectingRange, setSelectingRange] = useState(null);
  const [tempStartSelect, setTempStartSelect] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [tempCompareStartDate, setTempCompareStartDate] = useState(customCompareStartDate || '');
  const [tempCompareEndDate, setTempCompareEndDate] = useState(customCompareEndDate || '');
  const [tempCompareStartSelect, setTempCompareStartSelect] = useState(null);
  const [hoverCompareDate, setHoverCompareDate] = useState(null);
  const [compareCurrentMonth, setCompareCurrentMonth] = useState(() => {
    if (customCompareStartDate) {
      const start = new Date(customCompareStartDate);
      return new Date(start.getFullYear(), start.getMonth(), 1);
    }
    return new Date();
  });

  const modalRef = useRef(null);

  useEffect(() => {
    const period = periods.find((p) => p.key === selectedPeriod);
    if (period && period.key !== 'CUSTOM') {
      const range = calculateDateRange(selectedPeriod, period.days, customStartDate, customEndDate);
      if (range && range.startDate) {
        setCurrentMonth(new Date(range.startDate.getFullYear(), range.startDate.getMonth(), 1));
      }
    } else if (selectedPeriod === 'CUSTOM' && customStartDate) {
      const start = new Date(customStartDate);
      setCurrentMonth(new Date(start.getFullYear(), start.getMonth(), 1));
    }
  }, [selectedPeriod, customStartDate, customEndDate, periods]);

  useEffect(() => {
    if (isOpen) {
      setTempStartDate(customStartDate || '');
      setTempEndDate(customEndDate || '');
      setTempCompareStartDate(customCompareStartDate || '');
      setTempCompareEndDate(customCompareEndDate || '');
      if (customCompareStartDate) {
        const start = new Date(customCompareStartDate);
        setCompareCurrentMonth(new Date(start.getFullYear(), start.getMonth(), 1));
      } else {
        setCompareCurrentMonth(new Date());
      }
    }
  }, [isOpen, customStartDate, customEndDate, customCompareStartDate, customCompareEndDate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Cerrar al hacer click fuera del modal (listener en document para capturar todos los clics)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const getCurrentDates = () => {
    if (tempStartDate && tempEndDate) {
      const startParts = tempStartDate.split('-');
      const endParts = tempEndDate.split('-');
      const start = new Date(parseInt(startParts[0], 10), parseInt(startParts[1], 10) - 1, parseInt(startParts[2], 10));
      const end = new Date(parseInt(endParts[0], 10), parseInt(endParts[1], 10) - 1, parseInt(endParts[2], 10));
      return {
        start: startOfDay(start),
        end: endOfDay(end),
      };
    }
    if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
      const startParts = customStartDate.split('-');
      const endParts = customEndDate.split('-');
      const start = new Date(parseInt(startParts[0], 10), parseInt(startParts[1], 10) - 1, parseInt(startParts[2], 10));
      const end = new Date(parseInt(endParts[0], 10), parseInt(endParts[1], 10) - 1, parseInt(endParts[2], 10));
      return {
        start: startOfDay(start),
        end: endOfDay(end),
      };
    }
    const period = periods.find((p) => p.key === selectedPeriod);
    if (period) {
      const range = calculateDateRange(selectedPeriod, period.days, customStartDate, customEndDate);
      if (range && range.startDate && range.endDate) {
        const startDateOnly = new Date(range.startDate.getFullYear(), range.startDate.getMonth(), range.startDate.getDate());
        const endDateOnly = new Date(range.endDate.getFullYear(), range.endDate.getMonth(), range.endDate.getDate());
        return {
          start: startOfDay(startDateOnly),
          end: endOfDay(endDateOnly),
        };
      }
    }
    return { start: null, end: null };
  };

  const currentDates = getCurrentDates();

  const getCompareDates = () => {
    if (tempCompareStartDate && tempCompareEndDate) {
      const startParts = tempCompareStartDate.split('-');
      const endParts = tempCompareEndDate.split('-');
      const start = new Date(parseInt(startParts[0], 10), parseInt(startParts[1], 10) - 1, parseInt(startParts[2], 10));
      const end = new Date(parseInt(endParts[0], 10), parseInt(endParts[1], 10) - 1, parseInt(endParts[2], 10));
      return {
        start: new Date(start.getFullYear(), start.getMonth(), start.getDate()),
        end: new Date(end.getFullYear(), end.getMonth(), end.getDate()),
      };
    }
    if (customCompareStartDate && customCompareEndDate) {
      const startParts = customCompareStartDate.split('-');
      const endParts = customCompareEndDate.split('-');
      const start = new Date(parseInt(startParts[0], 10), parseInt(startParts[1], 10) - 1, parseInt(startParts[2], 10));
      const end = new Date(parseInt(endParts[0], 10), parseInt(endParts[1], 10) - 1, parseInt(endParts[2], 10));
      return {
        start: new Date(start.getFullYear(), start.getMonth(), start.getDate()),
        end: new Date(end.getFullYear(), end.getMonth(), end.getDate()),
      };
    }
    return null;
  };

  const comparisonDates =
    isCompareEnabled && currentDates && currentDates.start && currentDates.end
      ? (() => {
          if (compareType === 'custom') {
            const compareDates = getCompareDates();
            if (compareDates) return compareDates;
          }
          const start = currentDates.start;
          const end = currentDates.end;
          const diffTime = end - start;
          const diffDays = diffTime === 0 ? 1 : Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
          const prevEnd = new Date(start);
          prevEnd.setDate(prevEnd.getDate() - 1);
          const prevStart = new Date(prevEnd);
          prevStart.setDate(prevStart.getDate() - diffDays + 1);
          return { start: prevStart, end: prevEnd };
        })()
      : null;

  const getCalendarDays = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    const totalSlots = 42;
    const days = [];
    for (let i = 0; i < adjustedStartDay; i++) {
      days.push({ date: null, isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    while (days.length < totalSlots) {
      days.push({ date: null, isCurrentMonth: false });
    }
    return days;
  };

  const applyCompareSelection = (date) => {
    if (!isCompareEnabled || compareType !== 'custom') return;
    if (!tempCompareStartSelect) {
      setSelectingRange('compare');
      setTempCompareStartSelect(date);
      setTempCompareStartDate(formatDate(date, 'full-year-day'));
      setTempCompareEndDate('');
      setHoverCompareDate(null);
    } else {
      if (date < tempCompareStartSelect) {
        setTempCompareStartDate(formatDate(date, 'full-year-day'));
        setTempCompareEndDate(formatDate(tempCompareStartSelect, 'full-year-day'));
      } else {
        setTempCompareEndDate(formatDate(date, 'full-year-day'));
      }
      setTempCompareStartSelect(null);
      setHoverCompareDate(null);
      setSelectingRange(null);
    }
  };

  const handleDateClick = (date) => {
    if (isCompareEnabled && compareType === 'custom' && selectingRange === 'compare') {
      applyCompareSelection(date);
      return;
    }
    if (!selectingRange) {
      setSelectingRange('main');
      setTempStartSelect(date);
      setTempStartDate(formatDate(date, 'full-year-day'));
      setTempEndDate('');
      setHoverDate(null);
      if (selectedPeriod !== 'CUSTOM') onPeriodChange('CUSTOM');
    } else if (selectingRange === 'main') {
      if (date < tempStartSelect) {
        setTempStartDate(formatDate(date, 'full-year-day'));
        setTempEndDate(formatDate(tempStartSelect, 'full-year-day'));
      } else {
        setTempEndDate(formatDate(date, 'full-year-day'));
      }
      setSelectingRange(null);
      setHoverDate(null);
      if (selectedPeriod !== 'CUSTOM') onPeriodChange('CUSTOM');
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onCustomDatesChange(tempStartDate, tempEndDate);
      if (selectedPeriod !== 'CUSTOM') onPeriodChange('CUSTOM');
    } else if (selectedPeriod !== 'CUSTOM') {
      const period = periods.find((p) => p.key === selectedPeriod);
      if (period) {
        const range = calculateDateRange(selectedPeriod, period.days, customStartDate, customEndDate);
        if (range && range.startDateStr && range.endDateStr) {
          onCustomDatesChange(range.startDateStr, range.endDateStr);
        }
      }
    }
    if (tempCompareStartDate && tempCompareEndDate) {
      onCustomCompareDatesChange(tempCompareStartDate, tempCompareEndDate);
    }
    const appliedPeriodKey = selectedPeriod;
    const appliedPeriod = periods.find((p) => p.key === appliedPeriodKey);
    let appliedRange = null;
    if (tempStartDate && tempEndDate) {
      appliedRange = {
        startDateStr: tempStartDate,
        endDateStr: tempEndDate,
        startDate: new Date(tempStartDate),
        endDate: new Date(tempEndDate),
      };
    } else if (appliedPeriod && appliedPeriodKey !== 'CUSTOM') {
      appliedRange = calculateDateRange(appliedPeriodKey, appliedPeriod.days, customStartDate, customEndDate);
    }
    const appliedCompareRange =
      isCompareEnabled && compareType === 'custom' && tempCompareStartDate && tempCompareEndDate
        ? {
            startDateStr: tempCompareStartDate,
            endDateStr: tempCompareEndDate,
            startDate: new Date(tempCompareStartDate),
            endDate: new Date(tempCompareEndDate),
          }
        : null;
    if (appliedRange) {
      onApplyFilters({
        periodKey: appliedPeriodKey,
        days: appliedPeriod?.days ?? null,
        range: appliedRange,
        compareRange: appliedCompareRange,
        compareType,
      });
    }
    setSelectingRange(null);
    setHoverDate(null);
    setTempCompareStartSelect(null);
    setHoverCompareDate(null);
    onClose();
  };

  const handleCancel = () => {
    setTempStartDate(customStartDate || '');
    setTempEndDate(customEndDate || '');
    setSelectingRange(null);
    setHoverDate(null);
    setTempCompareStartDate(customCompareStartDate || '');
    setTempCompareEndDate(customCompareEndDate || '');
    setTempCompareStartSelect(null);
    setHoverCompareDate(null);
    onClose();
  };

  const isDateInRange = (date, range) => {
    if (!range || !range.start || !range.end) return false;
    return isWithinInterval(date, { start: range.start, end: range.end });
  };
  const isDateStart = (date, range) => (range && range.start ? isSameDay(date, range.start) : false);
  const isDateEnd = (date, range) => (range && range.end ? isSameDay(date, range.end) : false);
  const isDateInHoverRange = (date) => {
    if (!selectingRange || !tempStartSelect || !hoverDate) return false;
    const start = tempStartSelect < hoverDate ? tempStartSelect : hoverDate;
    const end = tempStartSelect < hoverDate ? hoverDate : tempStartSelect;
    return isWithinInterval(date, { start, end });
  };
  const _isDateInCompareHoverRange = (date) => {
    if (selectingRange !== 'compare' || !tempCompareStartSelect || !hoverCompareDate) return false;
    const start = tempCompareStartSelect < hoverCompareDate ? tempCompareStartSelect : hoverCompareDate;
    const end = tempCompareStartSelect < hoverCompareDate ? hoverCompareDate : tempCompareStartSelect;
    return isWithinInterval(date, { start, end });
  };

  const nextMonthDate = useMemo(() => addMonths(currentMonth, 1), [currentMonth]);
  const calendarDaysCurrent = useMemo(() => getCalendarDays(currentMonth), [currentMonth]);
  const calendarDaysNext = useMemo(() => getCalendarDays(nextMonthDate), [nextMonthDate]);
  const _compareCalendarDays = useMemo(() => getCalendarDays(compareCurrentMonth), [compareCurrentMonth]);
  const compareNextMonthDate = useMemo(() => addMonths(compareCurrentMonth, 1), [compareCurrentMonth]);
  const _compareCalendarDaysNext = useMemo(() => getCalendarDays(compareNextMonthDate), [compareNextMonthDate]);
  const weekDays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  const buildDayButtonClass = ({ isCurrentMonth, isFuture, mainFlags }) => {
    const classes = [
      'h-8 text-xs rounded-md transition-all duration-200',
      isCurrentMonth ? 'text-white' : 'text-white/30',
      isFuture ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/20 cursor-pointer',
    ];
    if (mainFlags) {
      const { isRange, isStart, isEnd, isHoverRange, isHoverEdge } = mainFlags;
      if (isRange) classes.push('bg-blue-500/30');
      if (isHoverRange) classes.push('bg-blue-400/20');
      if (isStart || isEnd) classes.push('bg-blue-500 font-bold');
      if (isHoverEdge) classes.push('bg-blue-400 font-semibold');
    }
    return classes.join(' ');
  };

  const _compareSelectionDates = compareType === 'custom' ? getCompareDates() : comparisonDates;

  const _handleCompareCalendarClick = (date) => {
    if (isAfter(date, endOfDay(today))) return;
    applyCompareSelection(date);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 modal-safe-area flex items-center justify-center z-[100002] p-4 pointer-events-none">
      <div
        ref={modalRef}
        className="enhanced-date-selector backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col my-auto pointer-events-auto"
      >
        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className="w-52 border-r border-white/20 p-3 overflow-y-auto shrink-0">
            <div className="space-y-1">
              {periods.map((period) => (
                <button
                  key={period.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPeriodChange(period.key);
                    if (period.key !== 'CUSTOM') {
                      setTempStartDate('');
                      setTempEndDate('');
                      setSelectingRange(null);
                      setHoverDate(null);
                      const range = calculateDateRange(period.key, period.days, customStartDate, customEndDate);
                      if (range && range.startDate) {
                        setCurrentMonth(new Date(range.startDate.getFullYear(), range.startDate.getMonth(), 1));
                      }
                    } else {
                      setSelectingRange('main');
                      setHoverDate(null);
                      if (customStartDate && customEndDate) {
                        setTempStartDate(customStartDate);
                        setTempEndDate(customEndDate);
                      } else {
                        setTempStartDate('');
                        setTempEndDate('');
                      }
                    }
                  }}
                  className={`w-full text-left px-2.5 py-1.5 text-xs rounded-lg transition-all duration-200 ${
                    selectedPeriod === period.key ? 'bg-blue-500/30 text-blue-100 font-medium' : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{period.label}</span>
                    {period.key === 'CUSTOM' && period.formattedLabel && selectedPeriod === 'CUSTOM' && (
                      <span className="ml-auto text-xs text-white/60">{period.formattedLabel}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 p-4 overflow-y-auto min-w-0">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentMonth(subMonths(currentMonth, 1));
                  }}
                  className="p-1.5 text-white/90 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex items-center gap-3 text-white font-medium text-sm">
                  <span>{format(currentMonth, 'MMM yyyy', { locale: es })}</span>
                  <span>{format(nextMonthDate, 'MMM yyyy', { locale: es })}</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentMonth(addMonths(currentMonth, 1));
                  }}
                  className="p-1.5 text-white/90 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-col gap-4 lg:flex-row">
                {[
                  { id: 'current', days: calendarDaysCurrent },
                  { id: 'next', days: calendarDaysNext },
                ].map(({ id, days }) => (
                  <div key={id} className="flex-1 min-w-0">
                    <div className="grid grid-cols-7 gap-0.5 mb-1">
                      {weekDays.map((day) => (
                        <div key={`${id}-${day}`} className="text-center text-[10px] font-medium text-white/70 py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {days.map((dayObj, idx) => {
                        if (!dayObj?.isCurrentMonth || !dayObj.date) {
                          return <div key={`${id}-${idx}`} className="h-8 rounded-md" />;
                        }
                        const date = dayObj.date;
                        const isFuture = isAfter(date, endOfDay(today));
                        const isMainRange = isDateInRange(date, currentDates);
                        const isMainStart = isDateStart(date, currentDates);
                        const isMainEnd = isDateEnd(date, currentDates);
                        const isHoverRange = isDateInHoverRange(date);
                        const isHoverStart = selectingRange === 'main' && tempStartSelect && isSameDay(date, tempStartSelect);
                        const isHoverEnd = selectingRange === 'main' && hoverDate && isSameDay(date, hoverDate);
                        return (
                          <button
                            key={`${id}-${idx}`}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isFuture) handleDateClick(date);
                            }}
                            onMouseEnter={() => {
                              if (selectingRange === 'main' && tempStartSelect && !isFuture) setHoverDate(date);
                            }}
                            onMouseLeave={() => setHoverDate(null)}
                            disabled={isFuture}
                            className={buildDayButtonClass({
                              isCurrentMonth: dayObj.isCurrentMonth,
                              isFuture,
                              mainFlags: {
                                isRange: isMainRange,
                                isStart: isMainStart,
                                isEnd: isMainEnd,
                                isHoverRange,
                                isHoverEdge: isHoverStart || isHoverEnd,
                              },
                            })}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 p-3 border-t border-white/20 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCancel();
            }}
            className="flex-1 px-3 py-1.5 text-sm text-white/90 bg-white/10 border border-white/30 rounded-lg hover:bg-white/20 transition-all duration-300"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleApply();
            }}
            className="flex-1 px-3 py-1.5 text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
